import { createContext, useContext } from 'react';

import { Facility, Professional } from '~/clients';
import logger from '~/logging';

import { useNetworks, useScopeInfo } from './app-state';

const log = logger(__filename);

type BaseDetailContext = {
  locationId: string;
  specialty: string;
  networkId: string;
};

type ProfessionalDetailContext = BaseDetailContext & {
  professional: Professional;
  facility?: never;
  showQualityIndicators: boolean;
};
export type FacilityDetailContext = BaseDetailContext & {
  professional?: never;
  facility: Facility;
  showQualityIndicators: false;
};
export type BasicProviderDetailContext = Partial<BaseDetailContext> & {
  professional?: Professional;
  facility?: Facility;
};

export type ProviderDetailContextType = ProfessionalDetailContext | FacilityDetailContext;

type BaseDetailsPageAction = {
  locationId?: string;
  specialty?: string;
  networkId?: string;
};

type UpdateProfessionalAction = BaseDetailsPageAction & {
  type: 'updateProfessional';
  professional: Professional;
  facility?: never;
};

type UpdateFacilityAction = BaseDetailsPageAction & {
  type: 'updateFacility';
  facility: Facility;
  professional?: never;
};
type UpdateLocationAction = BaseDetailsPageAction & {
  type: 'updateLocation';
  locationId: string;
};

export type DetailsPageAction = UpdateProfessionalAction | UpdateFacilityAction | UpdateLocationAction;

const dispatchBase: React.Dispatch<DetailsPageAction> = value => {
  return value;
};

export const ProviderDetailContext = createContext<BasicProviderDetailContext>({});

export const ProviderDetailDispatchContext = createContext(dispatchBase);

function useIsHcscCompliant(networkId: string | undefined) {
  const networks = useNetworks();
  const apiScope = useScopeInfo()?.plan;

  if (apiScope !== 'hcsc_compliant' || !networkId) return false;
  return !!networks.find(n => n.id === networkId)?.isHcscNetwork;
}

export function useProviderDetails(): ProviderDetailContextType | null {
  const { professional, facility, locationId, specialty, networkId } = useContext(ProviderDetailContext);
  const isHcscCompliant = useIsHcscCompliant(networkId);

  if ((!professional && !facility) || !locationId || !specialty || !networkId) {
    log.error(
      {
        professionalId: professional?.id,
        facilityId: facility?.id,
        locationId,
        strSpecialty: specialty,
        networkId,
      },
      'missing provider detail context',
    );
    return null;
  }
  if (professional) {
    return {
      professional,
      locationId,
      specialty,
      networkId,
      showQualityIndicators: !isHcscCompliant,
    };
  }
  if (facility) {
    return { facility, locationId, specialty, networkId, showQualityIndicators: false };
  }
  return null;
}

export function useProviderDetailDispatch() {
  return useContext(ProviderDetailDispatchContext);
}

export function providerDetailsReducer(
  details: BasicProviderDetailContext,
  action: DetailsPageAction,
): BasicProviderDetailContext {
  switch (action.type) {
    case 'updateProfessional': {
      return {
        ...details,
        ...action,
        facility: undefined,
      };
    }
    case 'updateFacility': {
      return {
        ...details,
        ...action,
        professional: undefined,
      };
    }
    case 'updateLocation': {
      return {
        ...details,
        locationId: action.locationId,
      };
    }
  }
}
